import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Avatar,
  AvatarGroup,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Styled Components
const DashboardContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  color: '#fff',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: theme.spacing(3),
  color: '#fff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.3rem',
  fontWeight: '600',
  marginBottom: theme.spacing(2),
  color: '#fff',
}));

const SearchBox = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: '#0f3460',
    borderRadius: theme.spacing(3),
    color: '#fff',
    border: '1px solid #00bcd4',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#00e5ff',
    },
    '&.Mui-focused': {
      borderColor: '#00bcd4',
      boxShadow: '0 0 20px rgba(0, 188, 212, 0.3)',
    },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: '#aaa',
    opacity: 1,
  },
}));

const KPICard = styled(Card)(({ theme }) => ({
  background: '#0f3460',
  border: '1px solid #1a4d6d',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    borderColor: '#00bcd4',
    boxShadow: '0 8px 24px rgba(0, 188, 212, 0.15)',
    transform: 'translateY(-4px)',
  },
}));

const KPIValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#00bcd4',
  marginBottom: theme.spacing(1),
}));

const KPILabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: '#aaa',
  marginBottom: theme.spacing(1),
}));

const KPISubtext = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: '#00e5ff',
}));

const ActivityCard = styled(Card)(({ theme }) => ({
  background: '#0f3460',
  border: '1px solid #1a4d6d',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#00bcd4',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 'bold',
  fontSize: '0.8rem',
  background: status === 'Success' ? '#4caf50' : '#ff9800',
  color: '#fff',
}));

const NewSpringDashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // Sample KPI data
  const kpiData = [
    {
      label: 'Deals via network',
      value: '24',
      subtext: '+15% last month',
      color: '#00bcd4',
    },
    {
      label: 'Top connectors',
      value: '3',
      subtext: 'Now this quarter',
      color: '#ffc107',
    },
    {
      label: 'Active intros',
      value: '12',
      subtext: '+3 last week',
      color: '#4caf50',
    },
    {
      label: 'Partners engaged',
      value: '8',
      subtext: 'Consistent',
      color: '#8bc34a',
    },
  ];

  // Sample chart data
  const chartData = [
    { month: 'Jan', value: 15 },
    { month: 'Feb', value: 10 },
    { month: 'Mar', value: 25 },
    { month: 'Apr', value: 18 },
  ];

  // Sample activity data
  const activityData = [
    {
      id: 1,
      title: 'Team received positive feedback for Michael Brown Intro',
      team: 'Team Outreach Squad',
      time: 'Last Week',
      status: 'Success',
      avatars: ['https://i.pravatar.cc/40?img=1', 'https://i.pravatar.cc/40?img=2'],
    },
    {
      id: 2,
      title: 'Introduction path explored by Team A: Jane Doe',
      team: 'Team: Outreach Squad',
      time: '1 Month ago',
      status: 'Reviewed',
      avatars: ['https://i.pravatar.cc/40?img=3', 'https://i.pravatar.cc/40?img=4'],
    },
    {
      id: 3,
      title: 'Team sent intro request for Alice Johnson at ExampleCorp',
      team: 'Team: Outreach Squad',
      time: '2 Months ago',
      status: 'Pending',
      avatars: ['https://i.pravatar.cc/40?img=5', 'https://i.pravatar.cc/40?img=6'],
    },
  ];

  return (
    <DashboardContainer>
      <Container maxWidth="xl">
        {/* Page Title */}
        <PageTitle>Partner Dashboard</PageTitle>

        {/* Quick Match Search */}
        <Box sx={{ marginBottom: 4 }}>
          <SectionTitle>Quick Match Search</SectionTitle>
          <SearchBox
            fullWidth
            placeholder="Enter company or linkedin URL"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: '#00bcd4', cursor: 'pointer' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Key Performance Indicators */}
        <Box sx={{ marginBottom: 4 }}>
          <SectionTitle>Key Performance Indicators</SectionTitle>
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            {kpiData.map((kpi, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <KPICard>
                  <CardContent>
                    <KPILabel>{kpi.label}</KPILabel>
                    <KPIValue sx={{ color: kpi.color }}>
                      {kpi.value}
                    </KPIValue>
                    <KPISubtext>{kpi.subtext}</KPISubtext>
                  </CardContent>
                </KPICard>
              </Grid>
            ))}
          </Grid>

          {/* Chart */}
          <Card
            sx={{
              background: '#0f3460',
              border: '1px solid #1a4d6d',
              borderRadius: 2,
              padding: 2,
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a4d6d" />
                <XAxis dataKey="month" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip
                  contentStyle={{
                    background: '#0f3460',
                    border: '1px solid #00bcd4',
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: '#00bcd4' }}
                />
                <Bar dataKey="value" fill="#00bcd4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Box>

        {/* Recent Introduction Activity */}
        <Box>
          <SectionTitle>Recent Introduction Activity</SectionTitle>
          {activityData.map((activity) => (
            <ActivityCard key={activity.id}>
              <CardContent sx={{ padding: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  {/* Avatars */}
                  <AvatarGroup max={2} sx={{ marginTop: 0.5 }}>
                    {activity.avatars.map((avatar, idx) => (
                      <Avatar key={idx} src={avatar} sx={{ width: 40, height: 40 }} />
                    ))}
                  </AvatarGroup>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: '500', marginBottom: 1, color: '#fff' }}
                    >
                      {activity.title}
                    </Typography>
                    <Chip
                      label={activity.team}
                      size="small"
                      sx={{
                        background: '#1a4d6d',
                        color: '#00bcd4',
                        marginBottom: 2,
                      }}
                    />
                  </Box>

                  {/* Time and Status */}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: '#aaa', marginBottom: 1 }}
                    >
                      {activity.time}
                    </Typography>
                    <StatusChip
                      label={activity.status}
                      status={activity.status}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </ActivityCard>
          ))}
        </Box>
      </Container>
    </DashboardContainer>
  );
};

export default NewSpringDashboard;